import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Archive,
  ImageIcon,
  Loader2,
  Mail,
  Play,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { authApi, formatApiError } from "../../../lib/api";
import { useConfirmDialog } from "../../../components/admin/ConfirmDialog";
import { PageToolbar } from "../../../components/admin/PageToolbar";
import RichTextEditor from "../../../components/admin/RichTextEditor";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Card } from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { Textarea } from "../../../components/ui/textarea";

const CATEGORIES = [
  { id: "trait", label: "Trait" },
  { id: "prophecy", label: "Prophecy" },
  { id: "fact", label: "Fact" },
  { id: "riddle", label: "Riddle" },
];

// Matches LOW_QUEUE_DAYS in supabase/functions/spool-daily-growth/pickSeed.ts
const LOW_QUEUE_DAYS = 7;

function queuedForCategory(rows, category) {
  return rows
    .filter((row) => row.category === category && row.status === "queued")
    .slice()
    .sort((a, b) => {
      const order = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
      if (order) return order;
      return String(a.created_at || "").localeCompare(String(b.created_at || ""));
    });
}

const STATUSES = [
  { id: "draft", label: "Draft" },
  { id: "queued", label: "Queued" },
  { id: "scheduled", label: "Scheduled" },
  { id: "published", label: "Published" },
  { id: "archived", label: "Archived" },
];

function lagosDate(offsetDays = 0) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Africa/Lagos",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const year = Number(parts.find((part) => part.type === "year")?.value);
    const month = Number(parts.find((part) => part.type === "month")?.value);
    const day = Number(parts.find((part) => part.type === "day")?.value);
    const dt = new Date(Date.UTC(year, month - 1, day));
    dt.setUTCDate(dt.getUTCDate() + offsetDays);
    return dt.toISOString().slice(0, 10);
  } catch {
    const dt = new Date();
    dt.setUTCDate(dt.getUTCDate() + offsetDays);
    return dt.toISOString().slice(0, 10);
  }
}

const emptyForm = () => ({
  category: "fact",
  title: "",
  body: "",
  scripture_ref: "",
  answer: "",
  status: "queued",
  sort_order: 0,
});

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function DailyGrowthAdminPage() {
  const { can } = useAuth();
  const canEdit = can("blog.posts", "edit");
  const canDelete = can("blog.posts", "delete");
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const [rows, setRows] = useState([]);
  const [queuedRows, setQueuedRows] = useState([]);
  const [scheduledRows, setScheduledRows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [imageDate, setImageDate] = useState(() => lagosDate(1));
  const [growthImage, setGrowthImage] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const imageInputRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [spooling, setSpooling] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    setLoading(true);
    try {
      const [items, set, runList, queued, scheduled] = await Promise.all([
        authApi.listDailyGrowth(
          filterCat === "all" ? null : filterCat,
          filterStatus === "all" ? null : filterStatus
        ),
        authApi.getDailyGrowthSettings(),
        authApi.listDailyGrowthRuns(14),
        authApi.listDailyGrowth(null, "queued"),
        authApi.listDailyGrowth(null, "scheduled"),
      ]);
      setRows(Array.isArray(items) ? items : []);
      setQueuedRows(Array.isArray(queued) ? queued : []);
      setScheduledRows(Array.isArray(scheduled) ? scheduled : []);
      setSettings(set || null);
      setRuns(Array.isArray(runList) ? runList : []);
    } catch (err) {
      toast.error(formatApiError(err.message) || "Could not load Daily Growth");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCat, filterStatus]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      category: row.category || "fact",
      title: row.title || "",
      body: row.body || "",
      scripture_ref: row.scripture_ref || "",
      answer: row.answer || "",
      status: row.status || "draft",
      sort_order: row.sort_order || 0,
      published_on: row.published_on || "",
    });
    setOpen(true);
  };

  const saveItem = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      await authApi.upsertDailyGrowth(editing?.id || null, form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.message) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this item?",
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await authApi.deleteDailyGrowth(id);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.message) || "Delete failed");
    }
  };

  const saveSettings = async (patch) => {
    try {
      const next = await authApi.updateDailyGrowthSettings(patch);
      setSettings(next);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.message) || "Could not save settings");
    }
  };

  const toggleCategory = (id) => {
    const current = Array.isArray(settings?.categories_enabled)
      ? [...settings.categories_enabled]
      : ["trait", "prophecy", "fact", "riddle"];
    const next = current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
    if (!next.length) {
      toast.error("Keep at least one category enabled");
      return;
    }
    saveSettings({ categories_enabled: next });
  };

  const runSpool = async ({ skipEmail = false, emailOnly = false } = {}) => {
    setSpooling(true);
    try {
      const result = await authApi.triggerDailyGrowthSpool({
        force: true,
        skipEmail,
        emailOnly,
      });
      const count = result?.spool?.count ?? result?.emails_sent ?? 0;
      toast.success(
        emailOnly
          ? `Digest sent (${result.emails_sent || 0} ok, ${result.emails_failed || 0} failed)`
          : skipEmail
            ? `Published ${count} item(s) for today`
            : `Spool done — ${count} published, ${result.emails_sent || 0} emails sent`
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err.message) || "Spool failed");
    } finally {
      setSpooling(false);
    }
  };

  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const row = await authApi.getDailyGrowthImage(imageDate);
        if (!cancelled) setGrowthImage(row?.image_url ? row : null);
      } catch (err) {
        if (!cancelled) {
          setGrowthImage(null);
          toast.error(formatApiError(err.message) || "Could not load the Daily Growth image");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imageDate, canEdit]);

  const onGrowthImageFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImageBusy(true);
    try {
      const previousPath = growthImage?.storage_path || "";
      const uploaded = await authApi.uploadDailyGrowthImage(file, imageDate);
      const saved = await authApi.upsertDailyGrowthImage(imageDate, uploaded.url, uploaded.path);
      setGrowthImage(saved?.image_url ? saved : { image_url: uploaded.url, storage_path: uploaded.path });
      if (previousPath && previousPath !== uploaded.path) {
        try {
          await authApi.removeStoredMedia(previousPath);
        } catch {
          /* the new image is already saved; the old file can be cleaned up later */
        }
      }
      toast.success("Daily Growth image saved");
    } catch (err) {
      toast.error(formatApiError(err.message) || "Could not upload the image");
    } finally {
      setImageBusy(false);
    }
  };

  const removeGrowthImage = async () => {
    const ok = await confirm({
      title: "Remove this Daily Growth image?",
      description: "The morning email for this date will send without an image.",
      confirmLabel: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    setImageBusy(true);
    try {
      const result = await authApi.deleteDailyGrowthImage(imageDate);
      if (result?.storage_path) {
        try {
          await authApi.removeStoredMedia(result.storage_path);
        } catch {
          /* the email no longer references the file */
        }
      }
      setGrowthImage(null);
      toast.success("Image removed");
    } catch (err) {
      toast.error(formatApiError(err.message) || "Could not remove the image");
    } finally {
      setImageBusy(false);
    }
  };

  const enabledCats = Array.isArray(settings?.categories_enabled)
    ? settings.categories_enabled
    : ["trait", "prophecy", "fact", "riddle"];

  return (
    <div>
      {confirmDialog}
      <PageToolbar
        className="mb-6"
        align="start"
        left={(
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-600" /> Daily Growth
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Around 9:00pm Nigeria time the next day&apos;s items are reserved as scheduled and stay off the public page. At 6:00am Nigeria time those reserved items are published and emailed; if a category was not reserved, the oldest queued item is used instead. An empty category is filled from a seed only when that title and scripture were not published in the last 365 days. Admins are emailed once a day when a queue drops below 7 days.
              Public:{" "}
              <a href="/blog?tab=daily-growth" className="text-red-600 hover:underline" target="_blank" rel="noreferrer">
                /blog?tab=daily-growth
              </a>
            </p>
          </div>
        )}
        right={canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={spooling} onClick={() => runSpool({ skipEmail: true })}>
              {spooling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Run spool now
            </Button>
            <Button variant="outline" disabled={spooling} onClick={() => runSpool({ emailOnly: true })}>
              {spooling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Send digest
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" /> Add item
            </Button>
          </div>
        ) : null}
      />

      {settings ? (
        <Card className="p-4 mb-6 border-0 shadow-md space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-gray-900">Spool settings</p>
              <p className="text-xs text-gray-500">
                Last run: {fmtWhen(settings.last_run_at)} · {settings.last_run_status || "—"} ·{" "}
                {settings.last_run_message || ""}
              </p>
            </div>
            <Button type="button" size="sm" variant="ghost" onClick={load}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <label className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
              <span className="text-sm font-medium">Cron spool enabled</span>
              <Switch
                checked={Boolean(settings.enabled)}
                disabled={!canEdit}
                onCheckedChange={(enabled) => saveSettings({ enabled })}
              />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
              <span className="text-sm font-medium">Email digest</span>
              <Switch
                checked={Boolean(settings.email_enabled)}
                disabled={!canEdit}
                onCheckedChange={(email_enabled) => saveSettings({ email_enabled })}
              />
            </label>
            <div className="space-y-1">
              <Label className="text-xs">Send time</Label>
              <p className="text-sm text-gray-800 rounded-md border border-gray-100 bg-white px-3 py-2">
                6:00 AM UK daily
              </p>
            </div>
            <div className="space-y-1 sm:col-span-2 lg:col-span-1">
              <Label className="text-xs">Email subject</Label>
              <Input
                value={settings.email_subject_template || ""}
                disabled={!canEdit}
                onChange={(e) => setSettings({ ...settings, email_subject_template: e.target.value })}
                onBlur={() =>
                  canEdit && saveSettings({ email_subject_template: settings.email_subject_template })
                }
              />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Categories to spool</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const on = enabledCats.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => toggleCategory(c.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      on ? "bg-red-100 text-red-700 border-red-200" : "bg-white text-gray-500 border-gray-200"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>
      ) : null}

      {canEdit ? (
        <Card className="p-4 mb-6 border-0 shadow-md">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <p className="font-semibold text-gray-900 flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-red-600" /> Email image
              </p>
              <p className="text-xs text-gray-500 mt-1 max-w-xl">
                Shown at the top of the 6:00am Nigeria Daily Growth email for the date you pick. If there is no image, or it fails to load, the email still sends. Riddle answers are never included.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={imageDate}
                onChange={(e) => setImageDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="w-full sm:w-64 h-36 rounded-xl border border-dashed border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
              {growthImage?.image_url ? (
                <img
                  src={growthImage.image_url}
                  alt={`Daily Growth ${imageDate}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <p className="text-xs text-gray-400 px-4 text-center">No image for {imageDate}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                className="hidden"
                onChange={onGrowthImageFile}
              />
              <Button
                type="button"
                variant="outline"
                disabled={imageBusy || !imageDate}
                onClick={() => imageInputRef.current?.click()}
              >
                {imageBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {growthImage?.image_url ? "Replace image" : "Upload image"}
              </Button>
              {growthImage?.image_url ? (
                <Button type="button" variant="ghost" className="text-red-600" disabled={imageBusy} onClick={removeGrowthImage}>
                  <Trash2 className="h-4 w-4 mr-2" /> Remove
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {settings ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {CATEGORIES.map((category) => {
            const items = queuedForCategory(queuedRows, category.id);
            const reserved = scheduledRows.filter((row) => row.category === category.id);
            const low = items.length < LOW_QUEUE_DAYS;
            return (
              <Card key={category.id} className="p-4 border-0 shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="font-semibold text-gray-900">{category.label}</p>
                  <Badge className={low ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>
                    {items.length} queued
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  {low
                    ? `Below ${LOW_QUEUE_DAYS} days of supply`
                    : `${items.length} days of supply`}
                </p>
                {reserved.length ? (
                  <p className="text-xs text-sky-700 mb-2">
                    Scheduled: {reserved.map((item) => `${item.published_on || "next"} · ${item.title || "Untitled"}`).join("; ")}
                  </p>
                ) : null}
                {items.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    Queue empty. The next run uses an unused seed, or skips this category if every title and scripture was published in the last year.
                  </p>
                ) : (
                  <ol className="text-sm text-gray-700 space-y-1 max-h-40 overflow-y-auto">
                    {items.map((item, index) => (
                      <li key={item.id} className="truncate">
                        <span className="text-gray-400 mr-1">{index + 1}.</span>
                        {item.title || "Untitled"}
                      </li>
                    ))}
                  </ol>
                )}
              </Card>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading…</p>
      ) : (
        <div className="space-y-3 mb-10">
          {rows.length === 0 ? (
            <p className="text-center text-gray-500 py-10">No items yet. Add queued content for the cron to pick.</p>
          ) : (
            rows.map((row) => (
              <Card key={row.id} className="p-4 border-0 shadow-sm flex flex-col sm:flex-row sm:items-start gap-3 justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap gap-2 mb-1">
                    <Badge variant="outline">{row.category}</Badge>
                    <Badge className={row.status === "queued" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}>
                      {row.status}
                    </Badge>
                    {row.published_on ? <Badge variant="secondary">{row.published_on}</Badge> : null}
                  </div>
                  <p className="font-semibold text-gray-900">{row.title}</p>
                  {row.scripture_ref ? <p className="text-xs text-gray-500 mt-0.5">{row.scripture_ref}</p> : null}
                </div>
                {canEdit ? (
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                    {row.status !== "archived" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => authApi.upsertDailyGrowth(row.id, { ...row, status: "archived" }).then(load)}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(row.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </Card>
            ))
          )}
        </div>
      )}

      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Recent runs</h3>
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Items</th>
                <th className="px-3 py-2">Emails</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                    No runs yet
                  </td>
                </tr>
              ) : (
                runs.map((r) => (
                  <tr key={r.id} className="border-t border-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{r.run_date}</td>
                    <td className="px-3 py-2">{Array.isArray(r.items) ? r.items.length : 0}</td>
                    <td className="px-3 py-2">
                      {r.emails_sent || 0} / fail {r.emails_failed || 0}
                    </td>
                    <td className="px-3 py-2">{r.status}{r.error ? ` — ${r.error}` : ""}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit item" : "Add Daily Growth item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(category) => setForm({ ...form, category })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(status) => setForm({ ...form, status })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Body</Label>
              <RichTextEditor value={form.body} onChange={(body) => setForm({ ...form, body })} />
            </div>
            <div className="space-y-1">
              <Label>Scripture reference</Label>
              <Input
                value={form.scripture_ref}
                onChange={(e) => setForm({ ...form, scripture_ref: e.target.value })}
                placeholder="e.g. John 11:35"
              />
            </div>
            {form.status === "scheduled" || form.status === "published" ? (
              <div className="space-y-1">
                <Label>{form.status === "scheduled" ? "Scheduled date" : "Published date"}</Label>
                <Input
                  type="date"
                  value={form.published_on || ""}
                  onChange={(e) => setForm({ ...form, published_on: e.target.value })}
                />
              </div>
            ) : null}
            {form.category === "riddle" ? (
              <div className="space-y-1">
                <Label>Answer</Label>
                <Textarea
                  rows={2}
                  value={form.answer}
                  onChange={(e) => setForm({ ...form, answer: e.target.value })}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={saving} onClick={saveItem}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
